import { ApolloError, gql } from '@apollo/client';
import { ChatStatus } from '@/enums';
import {
  PriceEditing_GetPriceEditDisplayInfoQuery,
  PriceEditing_GetPriceEditDisplayInfoQueryVariables,
} from '@/types';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { useOfflineOrderGuidePriceEditDisplayInfo } from '@/hooks/use-offline-data';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const PRICE_EDIT_DISPLAY_INFO_FRAGMENT = gql`
  fragment PriceEditing_PriceEditDisplayInfo on PriceEditDisplayInfoResponse {
    title_translations {
      title
      translation
    }
    variant_pack_details {
      detail {
        currency
        title
        trend
        type
        value
      }
      settings {
        disable_override_editing
        disable_sales_rep_info
        enable_peak_price_protection
      }
      variant_pack_id
    }
  }
`;

export const GET_PRICE_EDIT_DISPLAY_INFO = gql`
  query PriceEditing_GetPriceEditDisplayInfo(
    $deliveryDate: String
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getPriceEditDisplayInfo(
      delivery_date: $deliveryDate
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...PriceEditing_PriceEditDisplayInfo
    }
  }

  ${PRICE_EDIT_DISPLAY_INFO_FRAGMENT}
`;

export const GET_ORDER_GUIDE_PRICE_EDIT_DISPLAY_INFO = gql`
  query PriceEditing_GetOrderGuidePriceEditDisplayInfo(
    $deliveryDate: String
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getPriceEditDisplayInfo(
      delivery_date: $deliveryDate
      order_guide_only: true
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...PriceEditing_PriceEditDisplayInfo
    }
  }

  ${PRICE_EDIT_DISPLAY_INFO_FRAGMENT}
`;

export const useGetPriceEditDisplayInfo = (
  shouldFetch: boolean,
  onError?: (error: ApolloError) => void,
) => {
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { selectedFulfillment, fulfillmentHydrated } =
    useNewOrderActionsContext();
  const { selectedRestaurantRelationship } = useUserContext();
  const isProspect =
    selectedRestaurantRelationship.chatStatus === ChatStatus.PROSPECT;

  return useWrappedQuery<
    PriceEditing_GetPriceEditDisplayInfoQuery,
    PriceEditing_GetPriceEditDisplayInfoQueryVariables
  >(GET_PRICE_EDIT_DISPLAY_INFO, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
    onError,
    skip:
      !shouldFetch ||
      (!selectedFulfillment?.date && !isProspect) ||
      !fulfillmentHydrated,
    variables: {
      deliveryDate: selectedFulfillment?.date,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });
};

export const useGetOrderGuidePriceEditDisplayInfo = (
  shouldFetch: boolean,
  onError?: (error: ApolloError) => void,
) => {
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { selectedFulfillment, fulfillmentHydrated } =
    useNewOrderActionsContext();
  const { selectedRestaurantRelationship } = useUserContext();
  const isProspect =
    selectedRestaurantRelationship.chatStatus === ChatStatus.PROSPECT;
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const isOfflineModeActive = useOfflineModeActive();

  const onlinePriceEditDisplayInfo = useWrappedQuery<
    PriceEditing_GetPriceEditDisplayInfoQuery,
    PriceEditing_GetPriceEditDisplayInfoQueryVariables
  >(GET_ORDER_GUIDE_PRICE_EDIT_DISPLAY_INFO, {
    fetchPolicy: 'no-cache',
    onError,
    skip:
      !shouldFetch ||
      (!selectedFulfillment?.date && !isProspect) ||
      !fulfillmentHydrated,
    variables: {
      deliveryDate: selectedFulfillment?.date,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });
  const offlinePriceEditDisplayInfo = useOfflineOrderGuidePriceEditDisplayInfo(
    restaurantUUID!,
    supplierUUID!,
  );

  if (
    isOfflineModeActive &&
    isOfflineModeEnabled &&
    offlinePriceEditDisplayInfo.data
  ) {
    return offlinePriceEditDisplayInfo;
  }

  return onlinePriceEditDisplayInfo;
};
